import React, { useState, useEffect } from "react";

import { useAuth } from "../../auth/AuthContext";

import {
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  InputAdornment,
  Button,
  Box,
  Grid,
  Snackbar,
  Alert,
} from "@mui/material";

import { useClientInfo, axiosInstance } from "../../utils/commonImports";
import { createDocument } from "../../api/numberingApi";

import VehicleDropdown from "../../components/VehicleDropdown";
import VendorDropdown from "../../components/VendorDropdown";
import DriverDropdown from "../../components/DriverDropdown";
import CategoryDropdown from "../../components/CategoryDropdown";
import MyDatePicker from "../../components/MyDatePicker";

export default function IndentFormAdd({ onCancel, onSuccess, editData }) {
  const { clientId } = useClientInfo();
  const { token } = useAuth();

  const [form, setForm] = useState({
    fyid: 1,
    indent_number: "",
    indent_date: "",
    bunk_name: "",
    is_vendor_indent: false,
    vehicle_id: "",
    vehicle_no: "",
    vendor_vehicle_number: "",
    vehicle_group: "",
    vehicle_group_id: "",
    driver_id: "",
    driver_name: "",
    driver_mobile: "",
    current_km: "",
    previous_km_reading: "0",
    tank_balance: "0.00",
    fuel_type: "",
    fuel_qty: "0.00",
    fuel_rate: "0.00",
    fuel_amount: "0.00",
    oil_amount: "0.00",
    other_amount: "0.00",
    surcharge_perc: "0.00",
    surcharge_amt: "0.00",
    net_total: "0.00",
  });

  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: "", type: "success" });
  const [selectedBunk, setSelectedBunk] = useState(null);

  // Format to 2 decimals
  const format2Dec = (val) => {
    const num = parseFloat(val);
    if (isNaN(num)) return "0.00";
    return num.toFixed(2);
  };

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [dropdownData, setDropdownData] = useState({
    bunks: [],
    vehicles: [],
    vendors: [],
    vehicleGroups: [],
    drivers: [],
  });

  useEffect(() => {
    async function fetchDropdowns() {
      try {
        const clientId = 1; // 🔹 replace later with context variable if available

        const [bunkRes, vehRes, venRes, vgRes, driverRes] = await Promise.all([
          axiosInstance.get("/api/bunks", { params: { client_id: clientId } }),
          axiosInstance.get("/api/vehicles", {
            params: { client_id: clientId },
          }),
          axiosInstance.get("/api/vendors", {
            params: { client_id: clientId },
          }),
          axiosInstance.get("/api/components/category", {
            params: { type: "VehicleGroup", client_id: clientId },
          }),

          axiosInstance.get("/api/employees", {
            params: { client_id: clientId, employeedrivertype: "Driver" },
          }),
        ]);

        setDropdownData({
          bunks: bunkRes.data,
          vehicles: vehRes.data,
          vendors: venRes.data,
          vehicleGroups: vgRes.data,
          drivers: driverRes.data,
        });
      } catch (err) {
        console.error("Dropdown fetch failed:", err);
        setSnack({
          open: true,
          msg: "Failed to load dropdown data",
          type: "error",
        });
      }
    }
    fetchDropdowns();
  }, []);

  // ✅ Load edit data when editing an existing indent
  useEffect(() => {
    // 🔹 Run only when both editData & dropdownData are ready
    if (!editData) return;
    if (
      dropdownData.vehicles.length === 0 &&
      dropdownData.vendors.length === 0 &&
      dropdownData.bunks.length === 0
    ) {
      console.log("⏳ Waiting for dropdown data...");
      return;
    }

    console.log("🟢 Populating editData:", editData);

    // ✅ Flexible matching for IDs
    const findMatch = (arr, keys, id) =>
      arr.find((item) => keys.some((key) => item[key] === id)) || null;

    const selectedBunk = findMatch(
      dropdownData.bunks,
      ["bunkid", "id"],
      editData.bunk_id,
    );
    const selectedVehicle = findMatch(
      dropdownData.vehicles,
      ["vehicle_id", "vehicleid", "id"],
      editData.vehicle_id,
    );
    const selectedGroup = findMatch(
      dropdownData.vehicleGroups,
      ["categoryid", "id"],
      editData.vehicle_group_id,
    );
    const selectedDriver = findMatch(
      dropdownData.drivers,
      ["employeeid", "id"],
      editData.driver_id,
    );
    const selectedVendor = findMatch(
      dropdownData.vendors,
      ["vendorid", "id"],
      editData.vendor_id,
    );

    console.log("🚗 DEBUG — EditData.vehicle_id:", editData.vehicle_id);
    console.log("🚙 DEBUG — Dropdown vehicles:", dropdownData.vehicles);

    setForm((prev) => ({
      ...prev,
      ...editData,

      indent_date: editData.indent_date ? new Date(editData.indent_date) : null,

      fuel_qty: editData.fuel_qty ? String(editData.fuel_qty) : "0.00",
      fuel_rate: editData.fuel_rate ? String(editData.fuel_rate) : "0.00",
      fuel_amount: editData.fuel_amount ? String(editData.fuel_amount) : "0.00",
      oil_amount: editData.oil_amount ? String(editData.oil_amount) : "0.00",
      other_amount: editData.other_amount
        ? String(editData.other_amount)
        : "0.00",
      surcharge_perc: editData.surcharge_perc
        ? String(editData.surcharge_perc)
        : "0.00",
      surcharge_amt: editData.surcharge_amt
        ? String(editData.surcharge_amt)
        : "0.00",
      net_total: editData.net_total ? String(editData.net_total) : "0.00",

      // ✅ Bunk
      bunk_id: selectedBunk ? selectedBunk.bunkid : editData.bunk_id,
      bunk_name: selectedBunk
        ? selectedBunk.bunk_name
        : editData.bunk_name || "",

      // ✅ Vendor / Vehicle logic
      is_vendor_indent: !!editData.is_vendor_indent,

      vendor_id: selectedVendor
        ? selectedVendor.vendorid
        : editData.vendor_id || null,
      vendor_name: selectedVendor
        ? selectedVendor.vendorname
        : editData.vendor_name || "",

      vehicle_id: selectedVehicle
        ? selectedVehicle.vehicle_id ||
          selectedVehicle.vehicleid ||
          selectedVehicle.id
        : editData.vehicle_id || null,
      vehicle_no: selectedVehicle
        ? selectedVehicle.vehicle_no ||
          selectedVehicle.vehicle_number ||
          selectedVehicle.vehicle_reg_no ||
          ""
        : editData.vehicle_no || "",

      // ✅ Vehicle group
      vehicle_group_id: selectedGroup
        ? selectedGroup.categoryid
        : editData.vehicle_group_id || null,
      vehicle_group_name: selectedGroup
        ? selectedGroup.categoryname
        : editData.vehicle_group_name || "",

      // ✅ Driver info
      driver_id: selectedDriver
        ? selectedDriver.employeeid
        : editData.driver_id || null,
      driver_name: selectedDriver
        ? selectedDriver.employeefirstname || selectedDriver.employeename || ""
        : editData.driver_name || "",
      driver_mobile: selectedDriver
        ? selectedDriver.employeemobile || selectedDriver.employeemobileno || ""
        : editData.driver_mobile || "",

      current_km:
        editData.current_km ||
        editData.current_km_reading ||
        editData.current_reading ||
        "",
    }));

    console.log("✅ DEBUG — Selected Vehicle:", selectedVehicle);
    // ✅ Keep bunk stored for rate autofill
    setSelectedBunk(selectedBunk || null);
  }, [editData, dropdownData]);
  // ================= Auto Calculate Current Mileage =================
  useEffect(() => {
    const prevKm = parseFloat(form.previous_km_reading) || 0;
    const currKm = parseFloat(form.current_km) || 0;
    const fuelQty = parseFloat(form.fuel_qty) || 0;

    if (fuelQty > 0 && currKm > prevKm) {
      const mileage = (currKm - prevKm) / fuelQty;
      setForm((prev) => ({
        ...prev,
        current_mileage: mileage.toFixed(2),
      }));
    } else {
      setForm((prev) => ({ ...prev, current_mileage: "0.00" }));
    }
  }, [form.current_km, form.fuel_qty, form.previous_km_reading]);

  // ================= Handle Input Change =================
  const handleChange = (field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
  };

  // ================= Handle Blur for Numeric Fields =================
  const handleBlur = (field) => {
    const numericFields = [
      "fuel_qty",
      "fuel_rate",
      "fuel_amount",
      "oil_amount",
      "other_amount",
      "surcharge_perc",
      "surcharge_amt",
      "net_total",
    ];

    if (numericFields.includes(field)) {
      const num = parseFloat(form[field]);
      const formatted = isNaN(num) ? "0.00" : num.toFixed(2);
      setForm((p) => ({ ...p, [field]: formatted }));
    }
  };

  // ================= Auto Calculation =================
  useEffect(() => {
    const fuelQty = parseFloat(form.fuel_qty) || 0;
    const fuelRate = parseFloat(form.fuel_rate) || 0;
    const fuelAmount = fuelQty * fuelRate;

    const oilAmount = parseFloat(form.oil_amount) || 0;
    const otherAmount = parseFloat(form.other_amount) || 0;
    const surchargePerc = parseFloat(form.surcharge_perc) || 0;
    const surchargeAmt = (fuelAmount * surchargePerc) / 100;

    const netTotal = fuelAmount + oilAmount + otherAmount + surchargeAmt;

    setForm((prev) => ({
      ...prev,
      fuel_amount: format2Dec(fuelAmount),
      surcharge_amt: format2Dec(surchargeAmt),
      net_total: format2Dec(netTotal),
    }));
  }, [
    form.fuel_qty,
    form.fuel_rate,
    form.oil_amount,
    form.other_amount,
    form.surcharge_perc,
  ]);

  // ================= Validation =================
  const validateForm = () => {
    // if (!form.indent_number) return "Indent Number is required.";
    if (!form.indent_date) return "Indent Date is required.";
    if (!form.bunk_name) return "Bunk Name is required.";
    return null;
  };

  const handleVendorSelect = (value, vendor) => {
    handleChange("vendor_id", value);
    handleChange("vendor_name", vendor?.vendorname || "");
    // if you capture vendor vehicle no separately, do not auto-fill it here
  };

  const onVehicleChange = (vehicle) => {
    if (!vehicle) {
      handleChange("vehicle_id", null);
      handleChange("vehicle_group_id", null);
      handleChange("vehicle_group_name", "");
      handleChange("driver_id", null);
      handleChange("driver_name", "");
      handleChange("driver_mobile", "");
      handleChange("fixed_mileage", "0.00"); // ✅ Reset fixed mileage
      return;
    }

    const fullVehicle = dropdownData.vehicles.find(
      (v) => v.vehicle_id === vehicle.vehicle_id,
    );
    if (!fullVehicle) return;

    // Get Vehicle Group info
    const groupId = fullVehicle.vehicle_group_id || null;
    const groupObj = dropdownData.vehicleGroups.find(
      (g) => g.categoryid === groupId,
    );

    // ✅ Set form fields
    handleChange("vehicle_id", fullVehicle.vehicle_id);
    handleChange("vehicle_group_id", groupId);
    handleChange("vehicle_group_name", groupObj ? groupObj.categoryname : "");

    // ✅ Driver info
    handleChange("driver_id", fullVehicle.vehicle_driver_id || null);
    handleChange("driver_name", fullVehicle.vehicle_driver_name || "");
    handleChange("driver_mobile", fullVehicle.vehicle_driver_mobile_no || "");

    // ✅ Fixed mileage (new)
    handleChange(
      "fixed_mileage",
      fullVehicle.fixed_mileage ? fullVehicle.fixed_mileage.toString() : "0.00",
    );

    console.log(
      "🚗 Vehicle selected:",
      fullVehicle.vehicle_number,
      "| Fixed Mileage:",
      fullVehicle.fixed_mileage,
    );
  };

  const onDriverChange = (driverId) => {
    handleChange("driver_id", driverId);
    const driver = dropdownData.drivers.find((d) => d.employeeid === driverId);
    if (driver) {
      const fullName = [
        driver.employeefirstname,
        driver.employeemiddlename || "",
        driver.employeelastname || "",
      ]
        .filter(Boolean)
        .join(" ");
      handleChange("driver_name", fullName);
      handleChange("driver_mobile", driver.employeemobile || "");
    } else {
      handleChange("driver_name", "");
      handleChange("driver_mobile", "");
    }
  };

  // ================= Save Handler =================
  const handleSave = async () => {
    const error = validateForm();
    if (error) {
      setSnack({ open: true, msg: error, type: "error" });
      return;
    }

    if (loading) return;
    setLoading(true);

    let indentNumber = form.indent_number;

    try {
      // Generate new indent number only for NEW record
      if (!form.id) {
        const createdDoc = await createDocument(
          clientId,
          "Fuel Indent",
          "Fuel Indent Document",
        );
        if (!createdDoc?.document_number) {
          throw new Error("Numbering API failed to return a document number");
        }
        indentNumber = createdDoc.document_number;
      }

      // ✅ FIX: Map missing backend fields correctly
      const payload = {
        ...form,
        client_id: clientId,
        indent_number: indentNumber,
        // ✅ These must match backend column names
        current_km_reading: form.current_km || 0,
        previous_km_reading: form.previous_km_reading || 0,
        previous_fuel_qty: form.previous_fuel_qty || 0,
        tank_balance: form.tank_balance || 0,
        current_mileage: form.current_mileage || 0,
      };

      console.log("🚀 Final payload to backend:", payload); // ✅ debug log

      let res;
      if (form.id) {
        res = await axiosInstance.put(`/api/indents/${form.id}`, payload);
      } else {
        res = await axiosInstance.post("/api/indents", payload);
      }

      if (res.data.success) {
        setSnack({
          open: true,
          msg: form.id
            ? "Indent updated successfully!"
            : "Indent saved successfully!",
          type: "success",
        });

        setTimeout(() => {
          console.log("✅ Calling onSuccess callback...");
          if (onSuccess) onSuccess();
        }, 800);
      } else {
        throw new Error("Failed to save indent.");
      }
    } catch (err) {
      console.error("❌ Error saving indent:", err);
      setSnack({
        open: true,
        msg: err.message || "Error saving indent.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Cancel button handler
  const handleCancel = () => {
    console.log("🚪 Cancel clicked - going back");
    setForm({
      fyid: 1,
      indent_number: "",
      indent_date: "",
      bunk_name: "",
      is_vendor_indent: false,
      vehicle_id: "",
      vehicle_no: "",
      vendor_vehicle_no: "",
      vehicle_group: "",
      vehicle_group_id: "",
      driver_id: "",
      driver_name: "",
      driver_mobile: "",
      current_km: "",
      previous_km_reading: "0",
      tank_balance: "0.00",
      fuel_type: "",
      fuel_qty: "0.00",
      fuel_rate: "0.00",
      fuel_amount: "0.00",
      oil_amount: "0.00",
      other_amount: "0.00",
      surcharge_perc: "0.00",
      surcharge_amt: "0.00",
      net_total: "0.00",
    });

    if (onCancel) onCancel();
  };

  // ================= Styles =================
  const inputStyle = {
    fontSize: 14,
    "& .MuiInputBase-input": {
      fontSize: 14,
      textAlign: "right",
    },
    "& label": {
      fontSize: 13,
    },
  };

  const dropdownStyle = {
    fontSize: 14,
    "& .MuiSelect-select": {
      fontSize: 14,
      display: "flex",
      alignItems: "center",
    },
  };

  const noRowHoverStyle = {
    "&:hover": { backgroundColor: "transparent" },
  };

  useEffect(() => {
    console.log("🚗 DEBUG vehicle_id in form:", form.vehicle_id);
    console.log("🚙 dropdownData.vehicles:", dropdownData.vehicles);
    console.log("🟩 is_vendor_indent:", form.is_vendor_indent);
  }, [form.vehicle_id, dropdownData.vehicles, form.is_vendor_indent]);

  // ================= UI =================
  return (
    <Grid container spacing={1} padding={2}>
      <Grid item xs={12}>
        {/* ✅ Header Fields (Indent Number + Indent Date) */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 2,
            mb: 2,
            ml: 1,
          }}
        >
          {/* 🔹 Indent Number (Auto-generated, Read-only) */}
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <TextField
              label="Indent Number"
              size="small"
              variant="outlined"
              value={form.indent_number || ""}
              InputProps={{
                readOnly: true, // ✅ Prevent editing
              }}
              InputLabelProps={{
                shrink: true, // ✅ Fix label above
              }}
              sx={{
                width: 220,
                "& .MuiInputBase-root": {
                  height: 40,
                  fontSize: 14,
                  backgroundColor: "#f9f9f9", // 🔹 light gray to indicate readonly
                },
                "& .MuiInputLabel-root": {
                  fontSize: 13,
                  transform: "translate(14px, -10px) scale(1)",
                },
              }}
            />
          </Box>

          {/* 🔹 Indent Date */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              mt: 0.1,
            }}
          >
            <MyDatePicker
              label="Indent Date"
              value={form.indent_date ? new Date(form.indent_date) : null}
              onChange={(date) =>
                handleChange("indent_date", date ? date.toISOString() : null)
              }
              containerStyle={{
                width: 180,
                minHeight: 40,
              }}
              inputStyle={{
                fontSize: "0.9rem",
                padding: "8px 10px",
              }}
              labelStyle={{
                fontSize: "0.85rem",
                marginBottom: 4,
              }}
            />
          </Box>
        </Box>

        {/* ✅ Table Section */}
        <div
          style={{
            width: 1200,
            overflowX: "auto",
            overflow: "visible",
            borderRadius: 8,
            border: "1px solid #e0e0e0",
            backgroundColor: "#f8f9fb",
            padding: 12,
            boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
          }}
        >
          <Table
            sx={{
              backgroundColor: "#f8f9fb",
              "& tr": { height: 28, backgroundColor: "#f8f9fb" },
              "& td, & th": {
                backgroundColor: "#f8f9fb",
                paddingTop: 1.5,
                paddingBottom: 1.5,
                fontSize: 14,
                border: 0,
              },
            }}
          >
            <TableBody>
              {/* ✅ Bunk + Vendor Toggle */}
              <TableRow sx={noRowHoverStyle}>
                <TableCell colSpan={4}>
                  <Select
                    fullWidth
                    displayEmpty
                    value={form.bunk_name}
                    onChange={(e) => {
                      const bunk = dropdownData.bunks.find(
                        (b) => b.bunk_name === e.target.value,
                      );

                      handleChange("bunk_name", bunk?.bunk_name || "");
                      handleChange("bunk_id", bunk?.bunkid || null);
                      handleChange(
                        "fuel_surcharge",
                        bunk?.bunk_fuel_surcharge || 0,
                      );

                      // ✅ store full bunk object for later use
                      setSelectedBunk(bunk || null);
                    }}
                    size="small"
                    sx={{
                      ...inputStyle,
                      "& .MuiSelect-select": {
                        textAlign: "left",
                        fontSize: 14,
                        display: "flex",
                        alignItems: "center",
                        paddingLeft: "8px",
                      },
                    }}
                  >
                    <MenuItem value="">Select Bunk</MenuItem>
                    {dropdownData.bunks.map((b) => (
                      <MenuItem key={b.bunkid} value={b.bunk_name}>
                        {b.bunk_name}
                      </MenuItem>
                    ))}
                  </Select>
                </TableCell>
                <TableCell align="center">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.is_vendor_indent}
                        onChange={(e) =>
                          handleChange("is_vendor_indent", e.target.checked)
                        }
                        color="primary"
                      />
                    }
                    label="Vendor Indent"
                    sx={{
                      "& .MuiFormControlLabel-label": { fontSize: 13 },
                    }}
                  />
                </TableCell>
              </TableRow>

              {/* ✅ Vehicle or Vendor Selection Row */}
              <TableRow sx={noRowHoverStyle}>
                <TableCell>
                  {form.is_vendor_indent ? (
                 
                    <VendorDropdown
                      clientId={clientId}
                      vendorId={form.vendor_id} // ⭐ REQUIRED
                      onVendorChange={(v) => {
                        handleChange("vendor_id", v?.vendor_id || null);
                        handleChange("vendor_name", v?.vendor_name || "");
                      }}
                    />
                  ) : (
                    <VehicleDropdown
                      clientId={clientId}
                      vehicleNumber={form.vehicle_no || ""}
                      onVehicleChange={onVehicleChange}
                    />
                  )}
                </TableCell>

                {/* ✅ Vendor Vehicle Number — only visible if vendor indent is true */}
                {form.is_vendor_indent && (
                  <TableCell>
                    <TextField
                      fullWidth
                      label="Vendor Vehicle Number"
                      value={form.vendor_vehicle_number}
                      onChange={(e) =>
                        handleChange("vendor_vehicle_number", e.target.value)
                      }
                      size="small"
                      sx={inputStyle}
                    />
                  </TableCell>
                )}

                <TableCell>
                  <CategoryDropdown
                    clientId={clientId}
                    token={token}
                    categoryType="VehicleGroup"
                    name="vehicle_group_id"
                    label="Vehicle Group"
                    value={
                      form.vehicle_group_id !== null &&
                      form.vehicle_group_id !== ""
                        ? Number(form.vehicle_group_id)
                        : null
                    }
                    onChange={(value, category) => {
                      handleChange("vehicle_group_id", value);
                      handleChange(
                        "vehicle_group",
                        category?.categoryname || "",
                      );
                    }}
                  />
                </TableCell>

                <TableCell>
                  <DriverDropdown
                    clientId={clientId}
                    driverId={form.driver_id}
                    onDriverChange={onDriverChange}
                  />
                </TableCell>

                <TableCell>
                  <TextField
                    fullWidth
                    label="Driver Mobile"
                    value={form.driver_mobile}
                    onChange={(e) =>
                      handleChange("driver_mobile", e.target.value)
                    }
                    size="small"
                    sx={inputStyle}
                    InputProps={{ readOnly: true }}
                  />
                </TableCell>
              </TableRow>

              {/* ✅ Fuel Info */}
              <TableRow sx={noRowHoverStyle}>
                <TableCell>
                  <TextField
                    fullWidth
                    label="Current Km Reading"
                    value={form.current_km}
                    onChange={(e) => handleChange("current_km", e.target.value)}
                    size="small"
                    sx={inputStyle}
                  />
                </TableCell>
                {/* ✅ Fuel Type Dropdown */}
                <TableCell>
                  <Select
                    fullWidth
                    displayEmpty
                    value={form.fuel_type}
                    onChange={(e) => {
                      const selectedType = e.target.value;
                      handleChange("fuel_type", selectedType);

                      if (selectedBunk) {
                        let rate = 0;
                        switch (selectedType) {
                          case "Petrol":
                            rate = selectedBunk.fuel_petrol_rate || 0;
                            break;
                          case "Diesel":
                            rate = selectedBunk.fuel_desiel_rate || 0;
                            break;
                          case "CNG":
                            rate = selectedBunk.fuel_lpg_rate || 0;
                            break;
                          case "Electric":
                            rate = selectedBunk.fuel_charging_rate || 0;
                            break;
                          default:
                            rate = 0;
                        }

                        handleChange("fuel_rate", Number(rate).toFixed(2));
                      } else {
                        // No bunk selected yet
                        handleChange("fuel_rate", "0.00");
                      }
                    }}
                    size="small"
                    sx={{
                      ...inputStyle,
                      "& .MuiSelect-select": {
                        textAlign: "left",
                        fontSize: 14,
                        display: "flex",
                        alignItems: "center",
                        paddingLeft: "8px",
                      },
                    }}
                  >
                    <MenuItem value="">Select Fuel Type</MenuItem>
                    <MenuItem value="Petrol">Petrol</MenuItem>
                    <MenuItem value="Diesel">Diesel</MenuItem>
                    <MenuItem value="CNG">CNG</MenuItem>
                    <MenuItem value="Electric">Electric</MenuItem>
                  </Select>
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    label="Fuel Qty"
                    value={form.fuel_qty}
                    onChange={(e) => handleChange("fuel_qty", e.target.value)}
                    onBlur={() => handleBlur("fuel_qty")}
                    size="small"
                    sx={inputStyle}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    label="Fuel Rate"
                    value={form.fuel_rate}
                    onChange={(e) => handleChange("fuel_rate", e.target.value)}
                    onBlur={() => handleBlur("fuel_rate")}
                    size="small"
                    sx={inputStyle}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    label="Fuel Amount"
                    value={form.fuel_amount}
                    size="small"
                    InputProps={{ readOnly: true }}
                    sx={inputStyle}
                  />
                </TableCell>
              </TableRow>

              {/* ✅ Charges + Totals */}
              <TableRow sx={noRowHoverStyle}>
                <TableCell />
                <TableCell />
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>
                  Oil Charges
                </TableCell>
                <TableCell />
                <TableCell>
                  <TextField
                    fullWidth
                    placeholder="Oil Amount"
                    value={form.oil_amount}
                    onChange={(e) => handleChange("oil_amount", e.target.value)}
                    onBlur={() => handleBlur("oil_amount")}
                    size="small"
                    variant="outlined"
                    InputLabelProps={{ shrink: false }}
                    sx={{
                      ...inputStyle,
                      "& .MuiInputBase-input": { textAlign: "right" },
                    }}
                  />
                </TableCell>
              </TableRow>

              <TableRow sx={noRowHoverStyle}>
                <TableCell />
                <TableCell />
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>
                  Other Charges
                </TableCell>
                <TableCell />
                <TableCell>
                  <TextField
                    fullWidth
                    placeholder="Others Amount"
                    value={form.other_amount}
                    onChange={(e) =>
                      handleChange("other_amount", e.target.value)
                    }
                    onBlur={() => handleBlur("other_amount")}
                    size="small"
                    variant="outlined"
                    InputLabelProps={{ shrink: false }}
                    sx={{
                      ...inputStyle,
                      "& .MuiInputBase-input": { textAlign: "right" },
                    }}
                  />
                </TableCell>
              </TableRow>

              <TableRow sx={noRowHoverStyle}>
                <TableCell />
                <TableCell />
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>
                  Surcharge (%)
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    placeholder=""
                    value={form.surcharge_perc}
                    onChange={(e) =>
                      handleChange("surcharge_perc", e.target.value)
                    }
                    onBlur={() => handleBlur("surcharge_perc")}
                    size="small"
                    variant="outlined"
                    InputLabelProps={{ shrink: false }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">%</InputAdornment>
                      ),
                    }}
                    sx={{
                      ...inputStyle,
                      "& .MuiInputBase-input": { textAlign: "right" },
                    }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    placeholder="Surcharge Amount"
                    value={form.surcharge_amt}
                    size="small"
                    variant="outlined"
                    InputLabelProps={{ shrink: false }}
                    InputProps={{ readOnly: true }}
                    sx={{
                      ...inputStyle,
                      "& .MuiInputBase-input": { textAlign: "right" },
                    }}
                  />
                </TableCell>
              </TableRow>

              <TableRow sx={noRowHoverStyle}>
                <TableCell />
                <TableCell />
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>
                  Net Total
                </TableCell>
                <TableCell />
                <TableCell>
                  <TextField
                    fullWidth
                    placeholder="Net Total"
                    value={form.net_total}
                    size="small"
                    variant="outlined"
                    InputLabelProps={{ shrink: false }}
                    InputProps={{ readOnly: true }}
                    sx={{
                      ...inputStyle,
                      "& .MuiInputBase-input": {
                        textAlign: "right",
                        fontWeight: 600,
                        color: "#0a4b78",
                      },
                    }}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

        {/* ✅ Buttons */}
        <Box sx={{ display: "flex", gap: 2, ml: 2, mt: 4 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={loading}
          >
            {loading
              ? editData
                ? "Updating..."
                : "Saving..."
              : editData
                ? "Update"
                : "Save"}
          </Button>

          <Button variant="outlined" color="secondary" onClick={handleCancel}>
            Cancel
          </Button>
        </Box>

        {/* ✅ Snackbar */}
        <Snackbar
          open={snack.open}
          autoHideDuration={3000}
          onClose={() => setSnack({ ...snack, open: false })}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            severity={snack.type}
            onClose={() => setSnack({ ...snack, open: false })}
          >
            {snack.msg}
          </Alert>
        </Snackbar>
      </Grid>
    </Grid>
  );
}
